import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { PageHeader } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import EventForm, { emptyEvent } from "@/components/dashboard/EventForm";

export default function EventEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => setForm({ ...emptyEvent, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v != null)) })).catch(() => navigate("/events"));
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/events/${id}`, form);
      toast.success("Event updated");
      navigate(`/events/${id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!form) return <div className="p-8 max-w-2xl"><Skeleton className="h-96 rounded-xl" /></div>;

  return (
    <div className="p-5 sm:p-8 max-w-2xl">
      <Link to={`/events/${id}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to event
      </Link>
      <PageHeader title="Edit event" />
      <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6" data-testid="event-edit-form">
        <EventForm form={form} setForm={setForm} />
        <div className="flex gap-3 pt-6 mt-6 border-t border-border">
          <Button type="submit" disabled={loading} className="rounded-md" data-testid="event-edit-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
          <Button type="button" variant="outline" className="rounded-md" onClick={() => navigate(`/events/${id}`)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
